import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  Checkbox,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Groups as TeamIcon,
  AdminPanelSettings as AdminIcon,
  ViewColumn as ViewColumnIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { enIN } from 'date-fns/locale';
import { toast } from 'react-toastify';
import api from '../../utils/api';

function formatIndiaIso(ymd) {
  if (!ymd) return '—';
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map((x) => parseInt(x, 10));
  if (!y || !m || !d) return String(ymd);
  return format(new Date(y, m - 1, d, 12, 0, 0, 0), 'dd/MM/yyyy', { locale: enIN });
}

export default function TeamPtoApprovals({ adminView }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [qInput, setQInput] = useState('');
  const qRef = useRef(qInput);
  qRef.current = qInput;
  const [actionRow, setActionRow] = useState(null);
  const [actionType, setActionType] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [colMenuAnchorEl, setColMenuAnchorEl] = useState(null);
  const [visibleCols, setVisibleCols] = useState({
    employee: true,
    from: true,
    to: true,
    days: true,
    reason: true,
    status: true
  });

  const scope = adminView ? 'all' : 'team';
  const visibleColCount =
    (visibleCols.employee ? 1 : 0) +
    (visibleCols.from ? 1 : 0) +
    (visibleCols.to ? 1 : 0) +
    (visibleCols.days ? 1 : 0) +
    (visibleCols.reason ? 1 : 0) +
    (visibleCols.status ? 1 : 0) +
    1;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { scope };
      if (status) params.status = status;
      if (year) params.year = year;
      const qv = qRef.current.trim();
      if (qv) params.q = qv;
      const r = await api.get('/pto/approvals', { params });
      setRows(Array.isArray(r.data?.data?.requests) ? r.data.data.requests : []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not load PTO queue.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [scope, status, year]);

  useEffect(() => {
    load();
  }, [scope, status, year, adminView, load]);

  const openAction = (row, type) => {
    setActionRow(row);
    setActionType(type);
    setNote('');
  };

  const submitAction = async () => {
    if (!actionRow) return;
    setBusy(true);
    try {
      const path =
        actionType === 'approve'
          ? `/pto/approvals/${actionRow.id}/approve`
          : `/pto/approvals/${actionRow.id}/reject`;
      await api.post(path, { note: note.trim() || undefined });
      toast.success(actionType === 'approve' ? 'Approved.' : 'Rejected.');
      setActionRow(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Action failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        {adminView ? <AdminIcon color="primary" /> : <TeamIcon color="primary" />}
        {adminView ? 'PTO requests (organization)' : 'My Team PTO'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {adminView
          ? 'All employees’ leave requests. Approve or reject pending items.'
          : 'Leave requests from your reportees. Dates shown in India format (DD/MM/YYYY).'}
      </Typography>

      <Paper sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="rejected">Rejected</MenuItem>
        </TextField>
        <TextField
          size="small"
          type="number"
          label="Year"
          value={year}
          onChange={(e) => setYear(parseInt(e.target.value, 10) || new Date().getFullYear())}
          sx={{ width: 120 }}
        />
        <TextField
          size="small"
          label="Search employee"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()}
          sx={{ minWidth: 200 }}
        />
        <Button variant="outlined" onClick={load} disabled={loading}>
          Refresh
        </Button>
        <Tooltip title="Choose visible columns" arrow>
          <IconButton onClick={(e) => setColMenuAnchorEl(e.currentTarget)} color="primary" size="small">
            <ViewColumnIcon />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={colMenuAnchorEl}
          open={Boolean(colMenuAnchorEl)}
          onClose={() => setColMenuAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {[
            ['employee', 'Employee'],
            ['from', 'From'],
            ['to', 'To'],
            ['days', 'Days'],
            ['reason', 'Reason'],
            ['status', 'Status']
          ].map(([key, label]) => (
            <MenuItem
              key={key}
              onClick={() => setVisibleCols((prev) => ({ ...prev, [key]: !prev[key] }))}
              dense
            >
              <Checkbox size="small" checked={Boolean(visibleCols[key])} />
              <Typography variant="body2">{label}</Typography>
            </MenuItem>
          ))}
        </Menu>
      </Paper>

      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {visibleCols.employee && <TableCell>Employee</TableCell>}
                {visibleCols.from && <TableCell>From</TableCell>}
                {visibleCols.to && <TableCell>To</TableCell>}
                {visibleCols.days && <TableCell>Days</TableCell>}
                {visibleCols.reason && <TableCell>Reason</TableCell>}
                {visibleCols.status && <TableCell>Status</TableCell>}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColCount}>
                    <Typography variant="body2" color="text.secondary">
                      No requests match your filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id}>
                    {visibleCols.employee && <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.employee_name || '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.employee_email || ''}
                      </Typography>
                    </TableCell>}
                    {visibleCols.from && <TableCell>{formatIndiaIso(row.start_date)}</TableCell>}
                    {visibleCols.to && <TableCell>{formatIndiaIso(row.end_date)}</TableCell>}
                    {visibleCols.days && <TableCell>{row.calendar_days}</TableCell>}
                    {visibleCols.reason && <TableCell sx={{ maxWidth: 260, whiteSpace: 'normal' }}>{row.reason}</TableCell>}
                    {visibleCols.status && <TableCell>
                      <Chip
                        size="small"
                        label={row.status}
                        color={
                          row.status === 'approved' ? 'success' : row.status === 'rejected' ? 'error' : 'warning'
                        }
                      />
                    </TableCell>}
                    <TableCell align="right">
                      {row.status === 'pending' && (
                        <>
                          <Button size="small" color="success" onClick={() => openAction(row, 'approve')}>
                            Approve
                          </Button>
                          <Button size="small" color="error" sx={{ ml: 1 }} onClick={() => openAction(row, 'reject')}>
                            Reject
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={Boolean(actionRow)} onClose={() => !busy && setActionRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{actionType === 'approve' ? 'Approve leave' : 'Reject leave'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {actionRow?.employee_name} · {formatIndiaIso(actionRow?.start_date)} → {formatIndiaIso(actionRow?.end_date)}
          </Typography>
          <TextField
            fullWidth
            label="Note (optional)"
            multiline
            minRows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionRow(null)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" color={actionType === 'approve' ? 'success' : 'error'} onClick={submitAction} disabled={busy}>
            {busy ? <CircularProgress size={20} /> : actionType === 'approve' ? 'Confirm approve' : 'Confirm reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
